// The Factiii brand indigo — `rgb(var(--primary))` on web, `COLORS.primaryColor`
// on mobile, as a literal hex so color math (contrast, canvas fills) can use it.
// This is what factiiiId === 0, the "no factiii" sentinel, renders as. It is not
// theme-split: 4.46:1 on the dark page and 4.10:1 on the light one, so it clears
// the 3:1 floor either way.
export const FACTIII_BRAND_INDIGO = '#6366F1';

export type FactiiiColorTheme = 'dark' | 'light';

/**
 * Per-factiii identity colors, one palette per theme.
 *
 * These fill an avatar tile and a map pin, so the shape's edge has to be
 * discernible against the page — WCAG 1.4.11 wants 3:1 for a meaningful
 * graphic's boundary. Tailwind's 500 shades clear that comfortably on the dark
 * canvas (#08090A) at 3.90–10.09:1, but on the light canvas (#F5F5F5) six of
 * the thirteen fall under it: amber 1.97, lime 1.81, green 2.09, cyan 2.23,
 * teal 2.28, orange 2.57. The tile edge simply dissolved into the page.
 *
 * Light mode therefore uses the 700 shades, which range 4.58–7.25:1 against
 * #F5F5F5 — every entry passes, with the same hue order so a factiii stays
 * recognisably "the green one" in both themes. (The 600 shades were the first
 * candidate but still left amber at 2.92 and lime at 2.83.)
 *
 * The ink on top needs no theme branch of its own: getReadableTextColor
 * measures whichever tile it is handed, so it follows this automatically.
 */
const FACTIII_COLORS: Record<FactiiiColorTheme, readonly string[]> = {
  // Tailwind 500 — mid-tone, reads against the near-black dark canvas.
  dark: [
    '#ef4444',
    '#f97316',
    '#f59e0b',
    '#84cc16',
    '#22c55e',
    '#14b8a6',
    '#06b6d4',
    '#3b82f6',
    '#6366f1',
    '#8b5cf6',
    '#a855f7',
    '#d946ef',
    '#ec4899'
  ],
  // Tailwind 700 — deep enough to hold an edge against the light canvas.
  light: [
    '#b91c1c',
    '#c2410c',
    '#b45309',
    '#4d7c0f',
    '#15803d',
    '#0f766e',
    '#0e7490',
    '#1d4ed8',
    '#4338ca',
    '#6d28d9',
    '#7e22ce',
    '#a21caf',
    '#be185d'
  ]
};

/**
 * Generate a consistent color from a factiiiAvatarKey or factiiiId.
 *
 * `theme` defaults to 'dark' so callers that render outside the app's theme
 * keep the palette they already had — the OpenGraph image routes and the photo
 * watermark composers draw on their own canvas, not on the page, and must not
 * shift when a viewer flips their theme. Only in-app UI (the Avatar components)
 * passes a live theme.
 */
export const getFactiiiColor = (
  key: string | number,
  theme: FactiiiColorTheme = 'dark'
): string => {
  const palette = FACTIII_COLORS[theme];
  if (typeof key === 'number') {
    return palette[Math.abs(key) % palette.length];
  }
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }
  return palette[Math.abs(hash) % palette.length];
};
